import React from 'react';
import { Package, Truck, MapPin, Phone, Mail, Calendar, Clock, CheckCircle, Download, RotateCcw, AlertCircle } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { OrderTimeline } from '../components/OrderTimeline';
interface TrackingProps {
  onNavigate: (page: string) => void;
}
export function Tracking({
  onNavigate
}: TrackingProps) {
  const order = {
    id: 'PO-2023-001',
    date: '10 Okt 2023',
    status: 'in_transit',
    statusLabel: 'Dalam Pengiriman',
    items: 32,
    total: 4500000,
    courier: 'JNE Trucking',
    courierPhone: '+62 812-9999-8888',
    trackingNumber: 'JNE88291002',
    currentLocation: 'Gudang Transit Bogor',
    eta: 'Besok, 14:00 WIB',
    progress: 75,
    estimatedDelivery: '12 Okt 2023',
    shippingAddress: 'Jl. Raya Desa No. 123, RT 01/RW 02, Kec. Sukamaju, Kab. Bogor, Jawa Barat 16710',
    contactPerson: 'Budi Santoso',
    contactPhone: '+62 812-3456-7890'
  };
  const timeline = [{
    status: 'Pesanan Dibuat',
    timestamp: '10 Okt 2023, 09:15 WIB',
    location: 'Sistem KDMP',
    description: 'Pesanan berhasil dibuat dan menunggu konfirmasi',
    completed: true
  }, {
    status: 'Dikonfirmasi',
    timestamp: '10 Okt 2023, 10:30 WIB',
    location: 'PTX Warehouse',
    description: 'Pesanan dikonfirmasi dan siap diproses',
    completed: true
  }, {
    status: 'Dikemas',
    timestamp: '10 Okt 2023, 14:20 WIB',
    location: 'PTX Warehouse',
    description: '32 item berhasil dikemas dan siap dikirim',
    completed: true
  }, {
    status: 'Diserahkan ke Kurir',
    timestamp: '11 Okt 2023, 08:00 WIB',
    location: 'PTX Warehouse',
    description: 'Paket diserahkan ke JNE Trucking',
    completed: true
  }, {
    status: 'Dalam Pengiriman',
    timestamp: '11 Okt 2023, 10:30 WIB',
    location: 'Gudang Transit Bogor',
    description: 'Paket dalam perjalanan ke alamat tujuan',
    completed: true,
    current: true
  }, {
    status: 'Tiba di Tujuan',
    timestamp: 'Est. 12 Okt 2023, 14:00 WIB',
    location: 'Bogor, Jawa Barat',
    description: 'Paket akan tiba di alamat tujuan',
    completed: false
  }, {
    status: 'Terkirim',
    timestamp: 'Menunggu konfirmasi',
    location: 'Alamat Tujuan',
    description: 'Paket diterima oleh penerima',
    completed: false
  }];
  const orderItems = [{
    name: 'Indomie Goreng Special',
    sku: 'SKU-IM-001',
    quantity: 5,
    unit: 'Karton',
    price: 35000,
    subtotal: 175000
  }, {
    name: 'Aqua Galon 19L',
    sku: 'SKU-AQ-002',
    quantity: 3,
    unit: 'Galon',
    price: 22000,
    subtotal: 66000
  }, {
    name: 'Beras Ramos 5kg',
    sku: 'SKU-BR-005',
    quantity: 2,
    unit: 'Karung',
    price: 72000,
    subtotal: 144000
  }];
  return <div className="space-y-6" data-id="element-3443">
      {/* Header */}
      <div className="flex items-start justify-between" data-id="element-3444">
        <div data-id="element-3445">
          <h2 className="text-2xl font-bold text-slate-900" data-id="element-3446">
            Tracking Pesanan
          </h2>
          <p className="text-sm text-slate-500 mt-1" data-id="element-3447">
            Lacak status pengiriman pesanan Anda secara real-time
          </p>
        </div>
        <div className="flex gap-2" data-id="element-3448">
          <Button variant="outline" size="sm" icon={<Download className="w-4 h-4" data-id="element-3450" />} data-id="element-3449">
            Download PDF
          </Button>
          <Button variant="outline" size="sm" icon={<RotateCcw className="w-4 h-4" data-id="element-3452" />} data-id="element-3451">
            Pesan Ulang
          </Button>
        </div>
      </div>

      {/* Order Header */}
      <Card className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200" data-id="element-3453">
        <div className="flex items-start justify-between mb-4" data-id="element-3454">
          <div data-id="element-3455">
            <div className="flex items-center gap-3 mb-2" data-id="element-3456">
              <h3 className="text-2xl font-bold text-slate-900" data-id="element-3457">{order.id}</h3>
              <Badge variant="warning" className="flex items-center gap-1" data-id="element-3458">
                <Truck className="w-4 h-4" data-id="element-3459" />
                {order.statusLabel}
              </Badge>
            </div>
            <div className="flex items-center gap-4 text-sm text-slate-600" data-id="element-3460">
              <div className="flex items-center gap-1" data-id="element-3461">
                <Calendar className="w-4 h-4" data-id="element-3462" />
                <span data-id="element-3463">Dibuat: {order.date}</span>
              </div>
              <span className="text-slate-400" data-id="element-3464">•</span>
              <div className="flex items-center gap-1" data-id="element-3465">
                <Clock className="w-4 h-4" data-id="element-3466" />
                <span data-id="element-3467">ETA: {order.eta}</span>
              </div>
            </div>
          </div>
          <div className="text-right" data-id="element-3468">
            <p className="text-sm text-slate-600 mb-1" data-id="element-3469">Total Nilai</p>
            <p className="text-3xl font-bold text-blue-600" data-id="element-3470">
              Rp {order.total.toLocaleString('id-ID')}
            </p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-3" data-id="element-3471">
          <div className="flex justify-between items-center mb-2" data-id="element-3472">
            <span className="text-sm font-medium text-slate-700" data-id="element-3473">
              Progress Pengiriman
            </span>
            <span className="text-sm font-bold text-slate-900" data-id="element-3474">
              {order.progress}%
            </span>
          </div>
          <div className="w-full h-3 bg-white rounded-full overflow-hidden border border-blue-200" data-id="element-3475">
            <div className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-500" style={{
            width: `${order.progress}%`
          }} data-id="element-3476"></div>
          </div>
        </div>

        <div className="flex items-center gap-2 text-sm text-blue-800" data-id="element-3477">
          <MapPin className="w-4 h-4" data-id="element-3478" />
          <span className="font-medium" data-id="element-3479">Lokasi Saat Ini:</span>
          <span data-id="element-3480">{order.currentLocation}</span>
        </div>
      </Card>

      <div className="grid grid-cols-3 gap-6" data-id="element-3481">
        {/* Main Content */}
        <div className="col-span-2 space-y-6" data-id="element-3482">
          {/* Timeline */}
          <Card className="p-6" data-id="element-3483">
            <h3 className="font-bold text-lg text-slate-900 mb-6" data-id="element-3484">
              Timeline Pengiriman
            </h3>
            <div className="space-y-4" data-id="element-3485">
              {timeline.map((event, i) => <div key={i} className="flex gap-4" data-id="element-3486">
                  <div className="flex flex-col items-center" data-id="element-3487">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${event.completed ? 'bg-green-100 text-green-600' : 'bg-slate-100 text-slate-400'} ${event.current ? 'ring-4 ring-blue-200' : ''}`} data-id="element-3488">
                      {event.completed ? <CheckCircle className="w-5 h-5" data-id="element-3489" /> : <Clock className="w-5 h-5" data-id="element-3490" />}
                    </div>
                    {i < timeline.length - 1 && <div className={`w-0.5 h-16 ${event.completed ? 'bg-green-300' : 'bg-slate-200'}`} data-id="element-3491"></div>}
                  </div>
                  <div className="flex-1 pb-8" data-id="element-3492">
                    <div className="flex items-start justify-between mb-1" data-id="element-3493">
                      <h4 className={`font-bold ${event.completed ? 'text-slate-900' : 'text-slate-400'}`} data-id="element-3494">
                        {event.status}
                      </h4>
                      {event.current && <Badge variant="warning" className="text-xs" data-id="element-3495">
                          Sedang Berlangsung
                        </Badge>}
                    </div>
                    <p className={`text-sm ${event.completed ? 'text-slate-600' : 'text-slate-400'} mb-1`} data-id="element-3496">
                      {event.description}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-slate-500" data-id="element-3497">
                      <span data-id="element-3498">{event.timestamp}</span>
                      <span className="text-slate-400" data-id="element-3499">•</span>
                      <div className="flex items-center gap-1" data-id="element-3500">
                        <MapPin className="w-3 h-3" data-id="element-3501" />
                        <span data-id="element-3502">{event.location}</span>
                      </div>
                    </div>
                  </div>
                </div>)}
            </div>
          </Card>

          {/* Order Items */}
          <Card className="p-6" data-id="element-3503">
            <h3 className="font-bold text-lg text-slate-900 mb-4" data-id="element-3504">
              Detail Pesanan ({order.items} item)
            </h3>
            <div className="space-y-3" data-id="element-3505">
              {orderItems.map((item, i) => <div key={i} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg" data-id="element-3506">
                  <div className="flex items-center gap-4" data-id="element-3507">
                    <div className="w-12 h-12 bg-slate-200 rounded-lg flex items-center justify-center" data-id="element-3508">
                      <Package className="w-6 h-6 text-slate-400" data-id="element-3509" />
                    </div>
                    <div data-id="element-3510">
                      <h4 className="font-bold text-slate-900" data-id="element-3511">{item.name}</h4>
                      <p className="text-sm text-slate-600" data-id="element-3512">SKU: {item.sku}</p>
                    </div>
                  </div>
                  <div className="text-right" data-id="element-3513">
                    <p className="text-sm text-slate-600 mb-1" data-id="element-3514">
                      {item.quantity} {item.unit} × Rp{' '}
                      {item.price.toLocaleString('id-ID')}
                    </p>
                    <p className="text-lg font-bold text-slate-900" data-id="element-3515">
                      Rp {item.subtotal.toLocaleString('id-ID')}
                    </p>
                  </div>
                </div>)}
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6" data-id="element-3516">
          {/* Courier Info */}
          <Card className="p-6" data-id="element-3517">
            <h3 className="font-bold text-lg text-slate-900 mb-4" data-id="element-3518">
              Informasi Kurir
            </h3>
            <div className="space-y-4" data-id="element-3519">
              <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg" data-id="element-3520">
                <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center" data-id="element-3521">
                  <Truck className="w-6 h-6 text-white" data-id="element-3522" />
                </div>
                <div data-id="element-3523">
                  <p className="font-bold text-slate-900" data-id="element-3524">{order.courier}</p>
                  <p className="text-sm text-slate-600" data-id="element-3525">Ekspedisi</p>
                </div>
              </div>

              <div data-id="element-3526">
                <p className="text-xs text-slate-500 mb-1" data-id="element-3527">Nomor Resi</p>
                <p className="font-mono font-bold text-slate-900" data-id="element-3528">
                  {order.trackingNumber}
                </p>
              </div>

              <div className="pt-4 border-t border-slate-200" data-id="element-3529">
                <p className="text-xs text-slate-500 mb-3" data-id="element-3530">Kontak Kurir</p>
                <div className="space-y-2" data-id="element-3531">
                  <a href={`tel:${order.courierPhone}`} className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700" data-id="element-3532">
                    <Phone className="w-4 h-4" data-id="element-3533" />
                    <span data-id="element-3534">{order.courierPhone}</span>
                  </a>
                </div>
              </div>

              <Button variant="primary" size="sm" fullWidth icon={<Phone className="w-4 h-4" data-id="element-3536" />} data-id="element-3535">
                Hubungi Kurir
              </Button>
            </div>
          </Card>

          {/* Shipping Address */}
          <Card className="p-6" data-id="element-3537">
            <h3 className="font-bold text-lg text-slate-900 mb-4" data-id="element-3538">
              Alamat Pengiriman
            </h3>
            <div className="space-y-3" data-id="element-3539">
              <div data-id="element-3540">
                <p className="text-xs text-slate-500 mb-1" data-id="element-3541">Penerima</p>
                <p className="font-bold text-slate-900" data-id="element-3542">
                  {order.contactPerson}
                </p>
              </div>
              <div data-id="element-3543">
                <p className="text-xs text-slate-500 mb-1" data-id="element-3544">Alamat</p>
                <p className="text-sm text-slate-700 leading-relaxed" data-id="element-3545">
                  {order.shippingAddress}
                </p>
              </div>
              <div data-id="element-3546">
                <p className="text-xs text-slate-500 mb-1" data-id="element-3547">Telepon</p>
                <p className="text-sm text-slate-900" data-id="element-3548">{order.contactPhone}</p>
              </div>
            </div>
          </Card>

          {/* Help */}
          <Card className="p-6 bg-amber-50 border-amber-200" data-id="element-3549">
            <div className="flex items-start gap-3" data-id="element-3550">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-3551" />
              <div data-id="element-3552">
                <h4 className="font-bold text-amber-900 text-sm mb-1" data-id="element-3553">
                  Butuh Bantuan?
                </h4>
                <p className="text-xs text-amber-700 mb-3" data-id="element-3554">
                  Hubungi customer service jika ada kendala dengan pesanan Anda
                </p>
                <Button variant="outline" size="sm" fullWidth data-id="element-3555">
                  Hubungi CS
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>;
}